import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type {
  Banner,
  BannerListParams,
  BulkBannerIdsPayload,
  BulkBannerStatusPayload,
  CreateBannerPayload,
  PaginatedBanners,
  ReorderPayload,
  UpdateBannerPayload,
} from '@/types/banner';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildBannerListQuery(params: BannerListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const bannersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBanners: builder.query<PaginatedBanners, BannerListParams>({
      query: (params) => ({
        url: `/banners?${buildBannerListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildBannerListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildBannerListQuery(currentArg ?? {}) !== buildBannerListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Banners' as const, id })),
              { type: 'Banners', id: 'LIST' },
            ]
          : [{ type: 'Banners', id: 'LIST' }],
    }),
    getBanner: builder.query<Banner, string>({
      query: (id) => `/banners/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Banners', id }],
    }),
    createBanner: builder.mutation<Banner, CreateBannerPayload>({
      query: (body) => ({ url: '/banners/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Banners', id: 'LIST' }],
    }),
    updateBanner: builder.mutation<Banner, { id: string; body: UpdateBannerPayload }>({
      query: ({ id, body }) => ({ url: `/banners/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Banners', id },
        { type: 'Banners', id: 'LIST' },
      ],
    }),
    deleteBanner: builder.mutation<void, string>({
      query: (id) => ({ url: `/banners/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Banners', id: 'LIST' }],
    }),
    reorderBanners: builder.mutation<Banner[], ReorderPayload>({
      query: (body) => ({ url: '/banners/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Banners', id: 'LIST' }],
    }),
    bulkUpdateBannerStatus: builder.mutation<{ count: number }, BulkBannerStatusPayload>({
      query: (body) => ({ url: '/banners/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Banners', id: 'LIST' }],
    }),
    bulkDeleteBanners: builder.mutation<{ count: number }, BulkBannerIdsPayload>({
      query: (body) => ({ url: '/banners/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Banners', id: 'LIST' }],
    }),
    uploadBannerImage: builder.mutation<{ image: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/banners/upload-image`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ image: string }>(json) ? json.data : (json as { image: string });
          const message = isApiResponse<{ image: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetBannersQuery,
  useGetBannerQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  useReorderBannersMutation,
  useBulkUpdateBannerStatusMutation,
  useBulkDeleteBannersMutation,
  useUploadBannerImageMutation,
} = bannersApi;
